/**
 * Client wrapper for the Loom User Documents API.
 *
 * Uses the typed `userDocuments` export from `@amzn/loom-navbar-api`
 * which communicates via `window.loom.userDocuments` under the hood.
 *
 * Two use cases:
 *   1. Status Overview bar-visibility preferences (single doc)
 *   2. Saved filter sets (multi-doc CRUD)
 *
 * All calls are authenticated automatically via the navbar session.
 *
 * ⚠️ The User Documents API is currently beta-only. In prod, every
 * operation rejects with error code `FEATURE_DISABLED`. The public
 * helpers in this module catch that and degrade gracefully.
 */
import {
  userDocuments,
  isUserDocumentsError,
  waitForLoomNavbarLoaded,
} from "@amzn/loom-navbar-api";

// Re-export the error guard so callers don't need a second import.
export { isUserDocumentsError };

/** Service key registered in the Loom Service Registry. */
export const DACW_SERVICE_KEY = "devices-asin-central";

/** Namespace for Feature 1: status overview bar visibility prefs. */
export const NS_STATUS_OVERVIEW_PREFS = "status-overview-prefs";

/** Namespace for Feature 2: user-saved filter sets. */
export const NS_SAVED_FILTERS = "saved-filters";

// ---- Types (re-exported from @amzn/loom-navbar-api) ----

export type UserDocumentVisibility = "PRIVATE" | "PUBLIC";
export type UserDocumentRelationship =
  | "CO_OWNER"
  | "SHARED_EDIT"
  | "SHARED_READ"
  | "PUBLIC";
export type UserDocumentsErrorCode =
  | "VALIDATION"
  | "NOT_FOUND"
  | "FORBIDDEN"
  | "CONFLICT"
  | "RETRYABLE"
  | "FEATURE_DISABLED";

export interface UserDocument {
  docId: string;
  serviceKey: string;
  namespace: string;
  createdBy: string;
  visibility: UserDocumentVisibility;
  title?: string | null;
  body?: string | null;
  bodyVersion: number;
  schemaVersion?: number | null;
  relationship: UserDocumentRelationship;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CreateUserDocumentInput {
  serviceKey: string;
  namespace: string;
  body: string;
  title?: string;
  visibility?: UserDocumentVisibility;
  schemaVersion?: number;
}

export interface UpdateUserDocumentInput {
  docId: string;
  body?: string;
  title?: string;
  visibility?: UserDocumentVisibility;
  schemaVersion?: number;
  expectedVersion?: number;
}

export interface ListUserDocumentsInput {
  serviceKey: string;
  namespace: string;
  filter?: {
    relationship?: "MINE" | "SHARED" | "PUBLIC" | "ALL";
    visibility?: "PRIVATE" | "PUBLIC" | "ALL";
  };
  limit?: number;
  cursor?: string;
}

export interface ListUserDocumentsResult {
  items: UserDocument[];
  cursor?: string | null;
}

// ---- Initialization ----

/** Tracks whether waitForLoomNavbarLoaded() has resolved. */
let navbarReady = false;

/**
 * Ensures the navbar is loaded before calling the Documents API.
 * Idempotent — subsequent calls resolve immediately.
 */
async function ensureReady(): Promise<void> {
  if (navbarReady) return;
  try {
    await waitForLoomNavbarLoaded();
    navbarReady = true;
  } catch (e) {
    console.debug("[userDocumentsClient] Navbar not available:", e);
    throw e;
  }
}

// ---- Public API ----

/**
 * Create a new user document.
 */
export async function createDocument(
  input: CreateUserDocumentInput
): Promise<UserDocument> {
  await ensureReady();
  return userDocuments.create(input) as Promise<UserDocument>;
}

/**
 * Get a document by ID (returns null if not found or no access).
 */
export async function getDocument(docId: string): Promise<UserDocument | null> {
  await ensureReady();
  return userDocuments.get({ docId }) as Promise<UserDocument | null>;
}

/**
 * Update an existing document. Use `expectedVersion` for optimistic
 * concurrency when multiple tabs may write concurrently.
 */
export async function updateDocument(
  input: UpdateUserDocumentInput
): Promise<UserDocument> {
  await ensureReady();
  return userDocuments.update(input) as Promise<UserDocument>;
}

/**
 * Delete a document by ID.
 */
export async function deleteDocument(
  docId: string
): Promise<{ success: boolean }> {
  await ensureReady();
  return userDocuments.delete({ docId });
}

/**
 * List documents matching the given serviceKey + namespace.
 * Defaults to the caller's own documents (relationship: MINE).
 *
 * Note: listed documents never include `body`. Use `getDocument()`
 * to read a document's body.
 */
export async function listDocuments(
  input: ListUserDocumentsInput
): Promise<ListUserDocumentsResult> {
  await ensureReady();
  return userDocuments.list(input) as Promise<ListUserDocumentsResult>;
}

/**
 * Convenience: list all documents for a given namespace under the
 * DACW service key, filtered to the caller's own documents.
 */
export async function listMyDocuments(
  namespace: string,
  limit?: number
): Promise<UserDocument[]> {
  const result = await listDocuments({
    serviceKey: DACW_SERVICE_KEY,
    namespace,
    filter: { relationship: "MINE" },
    limit: limit ?? 50,
  });
  return result.items;
}

/**
 * Returns true when the User Documents runtime is available.
 * Use to gracefully degrade when running outside the Loom shell
 * (e.g., local dev without the navbar) or in prod where the API
 * is not yet enabled.
 */
export function isUserDocumentsAvailable(): boolean {
  if (typeof window === "undefined") return false;
  if (!window.loom?.loaded) return false;
  return typeof userDocuments?.create === "function";
}

/**
 * Returns the error code from a User Documents API rejection,
 * or undefined if the error is not a coded documents error.
 */
export function getErrorCode(e: unknown): UserDocumentsErrorCode | undefined {
  if (isUserDocumentsError(e)) {
    return (e as Error & { code: UserDocumentsErrorCode }).code;
  }
  return undefined;
}
